"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 8,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function NewArrivalsGrid() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="space-y-4">
            <div className="aspect-square bg-card animate-pulse" />
            <div className="h-4 bg-card animate-pulse w-2/3" />
            <div className="h-3 bg-card animate-pulse w-1/3" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16">
        <p className="text-sm italic text-muted-foreground mb-4">
          The inventory could not be reached.
        </p>
        <Button onClick={refetch} variant="outline" className="border-foreground text-foreground bg-transparent hover:bg-card">
          Try again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-center italic text-muted-foreground py-16">
        No items currently available.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((product, i) => {
      const catalogNumber = `OH.No ${String(14 + i).padStart(3, "0")}`;
      const ageRange = product.customFields?.age_range as string || "Standard";
      const isDiscounted = product.discounted_price !== undefined && product.discounted_price !== null && product.discounted_price < product.price;
      return <div key={product.id} data-index={i} className="group relative">
            <div className="relative bg-card border border-muted-foreground transition-all duration-300 ease-out rounded-md">
              <div className="absolute top-3 right-3 z-10 font-mono text-[0.65rem] tracking-[0.18em] uppercase text-foreground group-hover:text-accent transition-colors duration-200">
                {catalogNumber}
              </div>

              <Link to={product.link} className="block">
                <div className="aspect-square overflow-hidden bg-card">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-all duration-500 ease-out saturate-[0.92] group-hover:saturate-100" /> : <div className="h-full w-full bg-card" />}
                </div>
              </Link>

              <div className="p-5 pb-7 space-y-3">
                <Link to={product.link} className="block">
                  <h3 className="font-default text-lg font-semibold leading-snug text-foreground hover:text-accent transition-colors duration-200">
                    {product.title}
                  </h3>
                </Link>

                <p className="italic text-sm text-muted-foreground leading-tight">
                  {ageRange}
                </p>

                <div className="flex items-baseline gap-2 pt-1">
                  {isDiscounted ? <>
                      <span className="font-default text-lg font-medium text-foreground">
                        ${product.discounted_price?.toFixed(2)}
                      </span>
                      <span className="font-default text-sm line-through text-muted-foreground">
                        ${product.price.toFixed(2)}
                      </span>
                      <span className="text-accent font-default">*</span>
                    </> : <span className="font-default text-lg font-medium text-foreground">
                      ${product.price.toFixed(2)}
                    </span>}
                </div>

                <div className="pt-3">
                  <AddToCartButton productId={product.id} variant="default" size="sm" className="w-full" buttonClassName="bg-background text-white hover:bg-accent hover:text-background rounded-md font-default text-xs tracking-[0.15em] uppercase w-full" text="+ Add" showQuantity={false} />
                </div>
              </div>

              <div aria-hidden="true" className="absolute bottom-0 left-6 w-[3px] bg-accent h-[14px] group-hover:h-[22px] transition-all duration-300 ease-out" style={{
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 85%, 0 100%)"
          }} />
            </div>
          </div>;
    })}
    </div>;
}
export default function Shop() {
  return <section data-section-id="1236" id="fresh-from-the-shop" className="relative bg-background py-28 md:py-36 border-t border-accent">
      <div className="max-w-[1280px] mx-auto px-6 md:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <div className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground mb-6">
              OH.No 004 — New Arrivals
            </div>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground">
              Freshly{" "}
              <em className="italic text-accent font-bold">
                curated
              </em>{" "}
              for the stage
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-end gap-5">
            <p className="font-default italic text-base text-muted-foreground leading-relaxed">
              — Updated regularly, check back often
            </p>
            <Link to="/ShopPage" className="group inline-flex items-center gap-2 text-foreground font-default text-base self-start">
              <span className="relative">
                View all gear
                <span className="absolute left-0 -bottom-0.5 h-px w-full bg-accent transition-all duration-200 group-hover:h-[2px]" />
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-12">
          <div className="h-px flex-1 bg-foreground" />
          <span className="font-default text-accent text-lg leading-none">
            §
          </span>
          <div className="h-px flex-1 bg-foreground" />
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        order: "desc",
        orderby: "date"
      }}>
          <NewArrivalsGrid />
        </EcommerceProductsProvider>

        <div className="mt-16 pt-8 border-t border-foreground flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-default italic text-sm text-muted-foreground">
            * Marked items include limited-time discounts.
          </p>
          <div className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
            OH.No 004 / End of Spread
          </div>
        </div>
      </div>
    </section>;
}